import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ExternalLinkIcon, FlaskConicalIcon, MegaphoneIcon, MonitorIcon, UserRoundIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { SnakStill } from '../../SnakStill';
import { EmbedWindow } from '../EmbedWindow';
import { useCaseCandidates } from '../../../data/deck';
import type { UseCaseId } from '../../../data/deck';
const USE_CASE_ICONS: Record<UseCaseId, typeof VideoIcon> = {
  webinar: VideoIcon,
  website: MonitorIcon,
  campaign: MegaphoneIcon,
  congress: UsersIcon,
  clinical: FlaskConicalIcon,
  product: UserRoundIcon
};
const WEBSITE_SNAK_IMAGE = 'https://cdn.magicpatterns.com/uploads/5PTMtHKMbudksPzWj4rbys/Screenshot_2026-10-22_at_13.15.49.png';
const WEBSITE_SNAK_URL = 'https://ferring.snakker.io';
export function UseCasesSection() {
  const [websiteOpen, setWebsiteOpen] = useState(false);
  return <Section id="useCases" data-id="element-620" data-mp="2b48j41ns8la8">
      <AnimatePresence data-id="element-621" data-mp="2b2xgi562pjkp">
        {websiteOpen && <EmbedWindow key="website" url={WEBSITE_SNAK_URL} title="REKOVELLE website Snak" onClose={() => setWebsiteOpen(false)} data-id="element-622" data-mp="2fw0b8gml6z2h" />}
      </AnimatePresence>
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-623" data-mp="27487eos2io6v">
        <InView data-id="element-624" data-mp="2uu2f6rxj5mk9">
          <SectionHeadline lead="One format." follow="Many use cases." data-id="element-625" data-mp="2utj1xoitrmck" />
        </InView>
        <InView delay={0.1} data-id="element-626" data-mp="2ytb7rtgu3ee8">
          <p className="text-[14.5px] leading-[1.7] text-[var(--muted)]" data-id="element-627" data-mp="2ysi97fs13v76">
            If it matters, make it easier to engage with. Anything in the REKOVELLE story important enough to earn HCP
            attention can be Snakified.
          </p>
        </InView>
      </div>

      <ul className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-628" data-mp="19e3hqnpmlu7z">
        {useCaseCandidates.map((item, i) => {
        const Icon = USE_CASE_ICONS[item.id];
        return <InView key={item.id} delay={0.14 + i * 0.06} y={22} className="h-full" data-id="element-629" data-mp="ubd6bimifai1" data-mpi={i}>
              <li className="flex h-full flex-col rounded-[24px] bg-white p-5 ring-1 ring-[var(--fg)]/[0.08]" data-id="element-630" data-mp="qc4c6cnk5aly">
                <div className="flex items-start justify-between gap-4" data-id="element-631" data-mp="lj1ryv4o7dif">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[var(--brand)] text-white" data-id="element-632" data-mp="glrr8jbtzffa">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-633" data-mp="bnqcgalfu29m" />
                  </span>
                  {item.id === 'website' ? <button type="button" onClick={() => setWebsiteOpen(true)} aria-label="Open the REKOVELLE website Snak at ferring.snakker.io" className="group relative h-[150px] w-[220px] max-w-[70%] overflow-hidden rounded-[12px] ring-1 ring-[var(--fg)]/10 transition-shadow hover:ring-2 hover:ring-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-634" data-mp="2lh33o1woext">
                      <img src={WEBSITE_SNAK_IMAGE} alt="Website Snak screen: 'The goal isn't more follicles. It's the right response.' with too few, target and too many responses" className="h-full w-full object-cover object-left-top transition-transform duration-300 group-hover:scale-[1.03]" data-id="element-635" data-mp="24j0y7gvpmrof" />
                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-[var(--brand)] px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.14em] text-white shadow-md" data-id="element-636" data-mp="1lk57wgmz2xfv">
                        Open
                        <ExternalLinkIcon className="h-3 w-3" aria-hidden="true" data-id="element-637" data-mp="1ulwavdq4oq61" />
                      </span>
                    </button> : <div className="flex gap-2" data-id="element-638" data-mp="36007yss0iufb">
                      {item.media.map(media => <SnakStill key={media.alt} screen={media.screen} alt={media.alt} className="h-[150px] w-[78px]" data-id="element-639" data-mp="34ikz1f43e6lo" />)}
                    </div>}
                </div>
                <h3 className="mt-5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-640" data-mp="3ehk0acmj2zyn">{item.label}</h3>
                <p className="mt-1.5 text-[17px] font-bold leading-snug tracking-[-0.02em] text-[var(--fg)]" data-id="element-641" data-mp="39h4ovqlt42jb">{item.detail}</p>
              </li>
            </InView>;
      })}
      </ul>

      <InView delay={0.3} data-id="element-642" data-mp="3ihug0h04h4qp">
        <p className="mt-14 max-w-[44ch] border-t border-[var(--fg)]/10 pt-8 text-[24px] font-extrabold leading-[1.12] tracking-[-0.03em] text-[var(--fg)] sm:text-[30px]" data-id="element-643" data-mp="3iicbhbeynexk">
          Snakification is not about the asset type —{' '}
          <span className="text-[var(--brand)]" data-id="element-644" data-mp="a2j3jkyp3bs4">it’s about making important information easier to notice, understand and act on.</span>
        </p>
      </InView>
    </Section>;
}